import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { MapPin, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    postcode?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    country_code?: string;
  };
}

export interface AddressSearchResult {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  countryCode: string;
  latitude: string;
  longitude: string;
}

interface AddressSearchProps {
  onSelect: (address: AddressSearchResult) => void;
}

export function AddressSearch({ onSelect }: AddressSearchProps) {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    if (query.trim().length < 3) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
          { headers: { "Accept-Language": "en" }, signal: controller.signal },
        );
        if (!response.ok) throw new Error(`Address search failed: ${response.status}`);
        const data: NominatimResult[] = await response.json();
        if (!controller.signal.aborted) setResults(data);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error searching address:", error);
          toast.error(t("common:ErrorSearchingAddress"));
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, t]);

  const selectAddress = (result: NominatimResult) => {
    const address = result.address;
    onSelect({
      street: address.road || "",
      houseNumber: address.house_number || "",
      postalCode: address.postcode || "",
      city: address.city || address.town || address.village || address.municipality || "",
      countryCode: address.country_code?.toUpperCase() || "",
      latitude: result.lat || "",
      longitude: result.lon || "",
    });
    setQuery("");
    setResults([]);
    setIsSearching(false);
  };

  return (
    <div>
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Search className="text-muted-foreground h-4 w-4" />
        </div>
        <Input
          aria-label={t("common:SearchAddress")}
          placeholder={t("common:SearchAddress")}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setResults([]);
            setIsSearching(false);
          }}
          className="pl-9"
        />
        {isSearching && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
          </div>
        )}
      </div>
      {results.length > 0 && (
        <div className="bg-popover mt-2 rounded-md border shadow-lg">
          {results.map((result) => (
            <button
              key={result.place_id}
              type="button"
              onClick={() => selectAddress(result)}
              className="hover:bg-accent flex w-full items-start gap-2 border-b p-3 text-left text-sm last:border-b-0"
            >
              <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-foreground">{result.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
